// The ONLY place a minor-unit amount becomes a string.
//
// §06: every `_minor` field is an integer count of minor units in an explicit
// currency. Dividing by 100 at a call site is how a screen ends up disagreeing
// with an invoice — one caller rounds, another truncates, a third forgets the
// currency entirely and shows a USD figure with a CAD total. So there is one
// formatter, it always takes the currency, and it never sees a float.

import type { Currency, Money } from "@crm/shared";

const LOCALE = "en-CA";

/**
 * Minor-unit exponent per currency. Both currencies in the contract are
 * two-decimal, but keying it by `Currency` means adding a zero-decimal currency
 * (JPY) to the contract surfaces here as a missing key rather than as amounts
 * silently inflated by 100x.
 */
const FRACTION_DIGITS: Record<Currency, number> = {
  CAD: 2,
  USD: 2,
};

/**
 * CAD and USD share the `$` glyph, and this app shows both — sometimes in the
 * same table. The ISO code is therefore part of the rendered amount, not
 * decoration: "$1,250.00" alone is ambiguous here in a way it is not in an
 * app with a single currency.
 */
function integerFormatter(): Intl.NumberFormat {
  return new Intl.NumberFormat(LOCALE, { useGrouping: true, maximumFractionDigits: 0 });
}

export interface FormatMoneyOptions {
  /** Prefix non-negative amounts with `+`. For variance columns. */
  signDisplay?: "auto" | "exceptZero";
  /** Drop the trailing ISO code. Only for places that state the currency once. */
  omitCurrencyCode?: boolean;
}

/**
 * Format integer minor units as a human amount, e.g. `formatMoney(125000,
 * "CAD")` -> `"$1,250.00 CAD"`.
 *
 * The major and minor parts are split with integer arithmetic rather than
 * `minor / 100` so nothing ever passes through a binary float: 0.1 has no exact
 * double representation, and a report that sums a few hundred lines would
 * eventually be a cent off the number the database holds.
 */
export function formatMoney(
  minor: number,
  currency: Currency,
  options: FormatMoneyOptions = {},
): string {
  const digits = FRACTION_DIGITS[currency];
  const divisor = 10 ** digits;
  const whole = Math.trunc(minor);
  const abs = Math.abs(whole);
  const major = Math.trunc(abs / divisor);
  const fraction = abs % divisor;

  const sign = whole < 0 ? "-" : options.signDisplay === "exceptZero" && whole > 0 ? "+" : "";
  const fractionText = digits === 0 ? "" : `.${String(fraction).padStart(digits, "0")}`;
  const amount = `${sign}$${integerFormatter().format(major)}${fractionText}`;

  return options.omitCurrencyCode === true ? amount : `${amount} ${currency}`;
}

/** Same, for the contract's `{ amount_minor, currency }` pair. */
export function formatMoneyValue(money: Money, options: FormatMoneyOptions = {}): string {
  return formatMoney(money.amount_minor, money.currency, options);
}

/** One entry of a per-currency total: the currency, and the figure in it. */
export interface CurrencyTotal {
  currency: Currency;
  minor: number;
}

/**
 * A total that is a LIST, one figure per source currency, as every billing
 * surface now reports one.
 *
 * An invoice may not mix currencies, so a single figure over two of them is a
 * number no document could ever be raised for. The reports state them apart
 * and so does this: the figures are joined, each with its own code, and never
 * added.
 *
 * Null when the list is empty — a tile then draws its own em dash rather than
 * a `$0.00` that would claim a currency was in play and worth nothing.
 */
export function formatCurrencyTotals(totals: readonly CurrencyTotal[]): string | null {
  if (totals.length === 0) return null;
  return totals.map((total) => formatMoney(total.minor, total.currency)).join(" · ");
}

/**
 * Split percentages and win probabilities. `Percent` is a plain number 0-100
 * (the contract keeps split ARITHMETIC in Postgres `numeric`), so this is
 * display only — never multiply money by the result of this function.
 */
export function formatPercent(pct: number, maximumFractionDigits = 2): string {
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits }).format(pct)}%`;
}

/** An FX rate, at the 6 decimal places the contract stores. */
export function formatRate(rate: number): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(rate);
}

/** Plain integers — item counts, deal counts. */
export function formatCount(n: number): string {
  return integerFormatter().format(n);
}

/**
 * Decimal places a counted quantity carries.
 *
 * The interpretation rounds a quantity ONCE, to three places, and the columns
 * that hold one are `numeric(…,3)`. Printing fewer places is not a tidier
 * figure, it is a different one: "412.5 minutes at 0.80 comes to 330.00" is a
 * sum a reader can do, and "413 minutes at 0.80" is a sum that does not come
 * to what the line says.
 */
const QUANTITY_FRACTION_DIGITS = 3;

/**
 * A count of UNITS — minutes, calls, tokens, a prepaid balance. Fractional,
 * unlike `formatCount`, because the thing being counted is measured rather
 * than enumerated. Trailing zeros are dropped: a whole quantity reads whole.
 */
export function formatQuantity(quantity: number): string {
  return new Intl.NumberFormat(LOCALE, {
    useGrouping: true,
    maximumFractionDigits: QUANTITY_FRACTION_DIGITS,
  }).format(quantity);
}

const MONEY_INPUT_PATTERN = /^(-)?(\d*)(?:\.(\d*))?$/;

/**
 * The inverse of `formatMoney`, and the ONLY way a typed amount becomes minor
 * units. Returns `NaN` for anything it will not commit to, which the contract's
 * `z.int()` then reports on the right field — a form must never guess at an
 * amount.
 *
 * Parsed with integer arithmetic on the digit strings rather than
 * `Number(text) * 100`: 1.15 is not exactly representable as a double, and
 * `Math.round(1.15 * 100)` is 115 only by luck — `19.99 * 100` is
 * 1998.9999999999998. Off-by-one-cent amounts entered by hand are exactly the
 * bug this app cannot afford.
 *
 * More decimal places than the currency has is rejected rather than truncated:
 * silently dropping a digit someone typed is how $10.999 becomes $10.99 in the
 * database and an argument later.
 */
export function parseMoneyToMinor(input: string, currency: Currency): number {
  const digits = FRACTION_DIGITS[currency];
  const cleaned = input.replace(/[$,\s]/g, "");
  if (cleaned === "") return Number.NaN;

  const match = MONEY_INPUT_PATTERN.exec(cleaned);
  if (match === null) return Number.NaN;

  const [, sign, whole = "", fraction] = match;
  if (whole === "" && (fraction === undefined || fraction === "")) return Number.NaN;
  if (fraction !== undefined && fraction.length > digits) return Number.NaN;

  const major = whole === "" ? 0 : Number(whole);
  const minorPart =
    fraction === undefined || fraction === "" ? 0 : Number(fraction.padEnd(digits, "0"));
  const magnitude = major * 10 ** digits + minorPart;
  return sign === "-" ? -magnitude : magnitude;
}

/**
 * Minor units as a plain editable string — `125000` -> `"1250.00"`. Not
 * `formatMoney`: an input holding "$1,250.00 CAD" would have to be un-formatted
 * before it could be parsed again, and the grouping separators are exactly what
 * a keyboard doesn't produce.
 */
export function minorToInput(minor: number, currency: Currency): string {
  const digits = FRACTION_DIGITS[currency];
  const divisor = 10 ** digits;
  const whole = Math.trunc(minor);
  const abs = Math.abs(whole);
  const major = Math.trunc(abs / divisor);
  const fraction = abs % divisor;
  const sign = whole < 0 ? "-" : "";
  return digits === 0
    ? `${sign}${major}`
    : `${sign}${major}.${String(fraction).padStart(digits, "0")}`;
}

/**
 * A UNIT PRICE, which is a decimal string and not an integer.
 *
 * `unit_price_minor` is `numeric(18,9)` on the wire because an input token
 * costs a small fraction of one cent: an integer count of minor units would be
 * wrong by orders of magnitude, and a JSON number would be turned into a float
 * by half the readers of the API. So it arrives as text and it is rendered as
 * text — the decimal point is MOVED, never divided, so nothing here passes
 * through a binary float either.
 *
 * Trailing zeros go, down to the currency's own two places: "80.000000000"
 * minor is $0.80, and "0.000080000" minor is $0.0000008. A price that rounded
 * to $0.00 would say the unit is free, which is the one thing a rate card must
 * never say.
 */
export function formatUnitPrice(
  priceMinor: string,
  currency: Currency,
  options: FormatMoneyOptions = {},
): string {
  const shifted = shiftLeft(priceMinor, FRACTION_DIGITS[currency]);
  if (shifted === null) return priceMinor;
  const suffix = options.omitCurrencyCode === true ? "" : ` ${currency}`;
  return `${shifted}${suffix}`;
}

/**
 * A decimal string with its point moved `places` to the left, then trimmed —
 * string arithmetic, so an exact figure stays exact.
 *
 * Returns null for anything it will not commit to. A caller that gets null
 * prints what the server sent rather than a figure this build invented.
 */
function shiftLeft(value: string, places: number): string | null {
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (match === null) return null;
  const [, sign = "", whole = "0", fraction = ""] = match;
  const digits = `${whole}${fraction}`;
  const point = whole.length - places;
  const padded = point <= 0 ? `${"0".repeat(1 - point)}${digits}` : digits;
  const cut = point <= 0 ? 1 : point;
  const major = padded.slice(0, cut);
  const minor = padded.slice(cut);
  return `${sign}$${group(major)}.${trimMinor(minor, places)}`;
}

/** Thousands separators on the major part, by the same locale rule as `formatMoney`. */
function group(major: string): string {
  const parsed = Number(major);
  return Number.isFinite(parsed) ? integerFormatter().format(parsed) : major;
}

/**
 * The fractional digits, with trailing zeros removed but never below the
 * currency's own places: $0.80 reads as money, and $0.8 reads as a typo.
 */
function trimMinor(minor: string, places: number): string {
  const padded = minor.padEnd(places, "0");
  const trimmed = padded.replace(/0+$/, "");
  return trimmed.length < places ? padded.slice(0, places) : trimmed;
}

/** "$1.2M", "$840k": round figures read faster, on a board rather than a document. */
export function formatCompactMoney(minor: number, currency: Currency): string {
  const major = minor / 100;
  const abs = Math.abs(major);
  const sign = major < 0 ? "−" : "";
  if (abs >= 1_000_000)
    return `${sign}$${Number((abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2))}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  if (abs === 0) return "$0";
  return `${sign}${formatMoney(Math.abs(minor), currency, { omitCurrencyCode: true })}`;
}
